import { useEffect, useMemo } from 'react';
// material
import { CssBaseline } from '@mui/material';
import {
  alpha,
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles';
// hooks
import useSettings from '../hooks/useSettings';
//
import breakpoints from './breakpoints';
import GlobalStyles from './globalStyles';
import componentsOverride from './overrides';
import palette from './palette';
import shadows, { customShadows } from './shadows';
import shape from './shape';
import typography from './typography';

// ----------------------------------------------------------------------
export default function ThemeConfig({ children }) {
  const { themeMode, themeDirection, setColor } = useSettings();
  const isLight = themeMode === 'light';

  const themeOptions = useMemo(
    () => ({
      palette: isLight
        ? { ...palette.light, mode: 'light', primary: setColor }
        : { ...palette.dark, mode: 'dark', primary: setColor },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: {
        ...(isLight ? customShadows.light : customShadows.dark),
        primary: `0 8px 16px 0 ${alpha(setColor.main, 0.24)}`,
      },
    }),
    [isLight, themeDirection, setColor]
  );

  const theme = useMemo(() => {
    const base = createTheme(themeOptions);
    const aug = (color, shade) =>
      base.palette.augmentColor({
        color: { main: base.palette[color][shade] },
        name: `${color}.${shade}`,
      });
    const t = createTheme(base, {
      palette: {
        'info.lighter': aug('info', 'lighter'),
        'info.light': aug('info', 'light'),
        'info.dark': aug('info', 'dark'),
        'info.darker': aug('info', 'darker'),
        'success.lighter': aug('success', 'lighter'),
        'success.light': aug('success', 'light'),
        'success.dark': aug('success', 'dark'),
        'success.darker': aug('success', 'darker'),
        'warning.lighter': aug('warning', 'lighter'),
        'warning.light': aug('warning', 'light'),
        'warning.dark': aug('warning', 'dark'),
        'warning.darker': aug('warning', 'darker'),
        'error.lighter': aug('error', 'lighter'),
        'error.light': aug('error', 'light'),
        'error.dark': aug('error', 'dark'),
        'error.darker': aug('error', 'darker'),
      },
    });
    t.components = componentsOverride(t);
    return t;
  }, [themeOptions]);

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-mui-mode',
      isLight ? 'light' : 'dark'
    );
  }, [isLight]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
